<script setup>
/**
 * Single source of truth for rendering the Wise bank-transfer fields across the
 * partner portal and the merchant admin. The field schema itself comes from the
 * backend (PayoutBankTransfer::getFields()); this component only renders it.
 *
 * The parent owns the refresh + validation logic: it passes the current field
 * schema (`fields`) and the reactive `info` object (bank_transfer_info), and
 * listens for `refresh` to re-fetch the schema when a field that reshapes it
 * (currency, country) changes.
 */
import { watch } from 'vue';

const props = defineProps({
    // Wise field schema (PayoutBankTransfer::getFields() output).
    fields: { type: Array, default: () => [] },
    // Reactive bank_transfer_info object owned by the parent; mutated in place.
    info: { type: Object, required: true },
    // Map of field key -> truthy to highlight the field as invalid.
    errors: { type: Object, default: () => ({}) },
    // Optional field key -> localized label overrides (portal translations).
    labelMap: { type: Object, default: () => ({}) },
    // Label styling differs per surface (portal vs admin).
    labelClass: { type: String, default: 'block text-sm mb-1.5 opacity-80' },
    // Extra input/select class per surface (e.g. `rounded` or portal corners).
    inputExtraClass: { type: String, default: '' },
    disabled: { type: Boolean, default: false },
});

const emit = defineEmits(['refresh']);

// Guarantee a reactive slot exists for every field in the current schema so
// v-model binds correctly the moment a field appears (e.g. `address__state`
// once the country is US). Only absent keys are seeded — values the user has
// already entered are never overwritten — so refreshing the schema can never
// wipe entered data.
watch(
    () => props.fields,
    (fields) => {
        (fields || []).forEach((field) => {
            if (field?.key != null && !Object.prototype.hasOwnProperty.call(props.info, field.key)) {
                props.info[field.key] = field.valueDefault ?? null;
            }
        });
    },
    { immediate: true }
);

const borderClass = (key) => (props.errors?.[key]
    ? 'border-red-400'
    : 'border-gray-300 focus:border-gray-400');
</script>

<template>
    <template v-for="field in fields" :key="field.key">
        <div class="col-span-3">
            <label :class="labelClass">
                {{ labelMap[field.key] || field.name }}
            </label>

            <input
                v-if="field.type === 'text'"
                :name="field.key"
                type="text"
                :disabled="disabled"
                :class="['block w-full text-sm focus:border-gray-400 focus:ring-0', borderClass(field.key), inputExtraClass]"
                v-model="info[field.key]"
            >

            <select
                v-else-if="field.type === 'select'"
                :name="field.key"
                :disabled="disabled"
                v-model="info[field.key]"
                :class="['block w-full text-sm transition-shadow focus:ring-0 focus:border-gray-400', borderClass(field.key), inputExtraClass]"
                @change="emit('refresh', field)"
            >
                <option :value="null">--</option>
                <option v-for="value in field.valuesAllowed" :key="value.key" :value="value.key">
                    {{ value.name }}
                </option>
            </select>

            <radio-group
                v-else-if="field.type === 'radio'"
                :name="field.key"
                :disabled="disabled"
                :model-value="info[field.key]"
                :options="(field.valuesAllowed || []).map((value) => ({ value: value.key, label: value.name }))"
                @update:model-value="(event) => { info[field.key] = event; emit('refresh', field); }"
            ></radio-group>
        </div>
    </template>
</template>
